<template>
	<section id="printcover">
		<ButtonSticky @buttonclick="exportPdf" v-if="!loading && filteredCases.length > 0" :width="250">
			<template v-slot:icon><PdfIcon :color="'#fff'" class="el-icon" /></template>
			<template v-slot:text> Bericht als PDF exportieren </template>
		</ButtonSticky>

		<h1>Bericht</h1>

		<h2 v-if="request?.fromDate && request?.toDate"> Zeitraum: {{ $formatDate(request.fromDate) }} - {{ $formatDate(request.toDate) }} </h2>

		<div v-if="parsedCriteria.length > 0" class="report__criteria">
			<span>Filterkriterien:</span>
			<ul>
				<li v-for="c in parsedCriteria" :key="'criteria-' + c.label" class="has-status has-status--grey"> {{ c.label }}: {{ c.value }} </li>
			</ul>
		</div>
	</section>

	<div class="bg--white flex flex-grow" id="bericht-items">
		<section id="searchresults" v-if="Object.entries(request).length > 0">
			<template v-if="loading">
				<strong class="flex"><LoadingIcon /> Bericht wird erstellt.</strong>

				<p>{{ filteredCases.length }} von {{ casesTotalCount }} Fällen geladen...</p>
			</template>

			<template v-else-if="filteredCases.length > 0">
				<div v-for="c in filteredCases" :key="c.id" class="bericht-item">
					<el-row class="titlerow">
						<el-col :sm="12">
							<h2>{{ c.title }}</h2>
						</el-col>
						<el-col :sm="12">
							<div :class="`has-status has-status--${$getStateColor('case', c.state)}`">
								{{ $getStateFullLabel('case', c.state, false) }}
							</div>
						</el-col>
					</el-row>

					<!-- <RouterLink :to="'/fall/' + c.id" :title="'Fall anzeigen'" class="link"> Fall anzeigen </RouterLink> -->

					<h3 class="no-marginbottom">Fall Infos</h3>
					<div class="datagrid">
						<el-row>
							<el-col :sm="12" :xs="24">
								<el-form-item label="Falltitel:" prop="title">
									<span>{{ c.title }}</span>
								</el-form-item>

								<el-form-item label="Medium:" prop="mediums">
									<span>{{ c.mediums.map((medium) => medium.name).join(', ') }}</span>
								</el-form-item>
							</el-col>

							<el-col :sm="12" :xs="24">
								<el-form-item label="Beschwerdegrund:" prop="category">
									<span>{{ c.category.name }}</span>
								</el-form-item>

								<el-form-item label="Reichweite:" prop="mediums">
									<span>{{ c.reaches.length > 0 ? c.reaches.map((medium) => medium.name).join(', ') : '-' }}</span>
								</el-form-item>
							</el-col>
						</el-row>
					</div>

					<div :class="`bericht-item__complaints ${c.complaints.length > 1 ? 'bericht-item__complaints--multiple' : ''}`">
						<h3>Beschwerden:</h3>
						<TheBox v-for="b in c.complaints" :key="b.id">
							<div class="text">{{ b.text }}</div>
						</TheBox>
					</div>

					<div :class="`bericht-item__media`" v-if="c.caseFiles?.length > 0">
						<h3>Medien:</h3>
						<TheBox :alignment="'center'" v-for="f in c.caseFiles">
							<a :href="getShowUrl(f)" target="_blank" title="Datei in neuem Tab öffnen">
								<PdfIcon v-if="isPdf(f.file)" class="bericht-item__media--pdficon" />
								<AudioIcon v-else-if="isAudio(f.file)" />
								<ImageIcon v-else-if="isImage(f.file)" />
								<VideoIcon v-else-if="isVideo(f.file)" />
								<span class="filename">{{ fileNameWithoutExtension(f.file.fileName) }}{{ fileExtension(f.file.fileName) }}</span>
								<span class="filename">Medium anzeigen</span>
							</a>
						</TheBox>
					</div>

					<div class="bericht-item__decision" v-if="hasDecision(c)">
						<h3>{{ isSmallSenateDecision(c) ? 'Senatsentscheidung' : 'Endgültige Entscheidung:' }}</h3>
						<div :class="`has-status has-status--${$getStateColor('case', c.state)}`">
							{{ $getStateFullLabel('case', c.state, false) }}
						</div>

						<h4>Begründung:</h4>
						<div class="text">{{ c.reasonText ? c.reasonText : '-' }}</div>

						<el-row v-if="c.decidedDate" class="decided-info">
							<el-text>Entscheidung getroffen: {{ $formatDate(c.decidedDate, 'HH:MM, DD.MM.YYYY') }}</el-text>
						</el-row>
						<el-row v-if="c.publishedDate" class="decided-info">
							<el-text>Veröffentlicht am: {{ $formatDate(c.publishedDate, 'HH:MM, DD.MM.YYYY') }}</el-text>
						</el-row>
					</div>

					<BerichtItemStatistic :statistics="getStatisticByCaseId(c.id)" :caseObject="c" />
				</div>
			</template>
			<el-alert v-else :title="'Fälle konnten leider nicht geladen werden.'" type="error" :closable="false" />
		</section>
		<section id="searchresults" v-else>
			<h2>Der Bericht wurde bereits durch eine neue Abfrage überschrieben.</h2>

			<p>Bitte führen Sie mit den gleichen Filtereinstellungen erneut eine Abfrage durch, um den Bericht wieder zu generieren.</p>
			<RouterLink to="/statistik" class="link">Zurück zur Berichtserstellung</RouterLink>
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { SearchCaseRequest, CaseResponse, CaseState, StatisticResponseWithCaseId, DecisionType, DecisionState } from '@/types/types';
import { getCaseStatistics } from '@/requests/cases';
import LoadingIcon from '@/assets/icons/LoadingIcon.vue';
import TheBox from '@/components/TheBox.vue';
import BerichtItemStatistic from '@/views/sections/BerichtItemStatistic.vue';
import Wahlbeteiligung from '@/components/charts/Wahlbeteiligung.vue';
import PieChart from '@/components/charts/PieChart.vue';
import CategoryBarChart from '@/components/charts/CategoryBarChart.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import PdfIcon from '../assets/icons/PdfIcon.vue';
import AudioIcon from '../assets/icons/AudioIcon.vue';
import ImageIcon from '../assets/icons/ImageIcon.vue';
import VideoIcon from '../assets/icons/VideoIcon.vue';

import { prepareSearchRequest } from '@/utils/search/searchUtils';
import { searchCases } from '@/requests/searches.js';

import { getCallbacksCount, getCaseVoteEnde, isSmallSenateDecision, hasDecision } from '@/utils/casesHandling/cases';
import { getCaseFiles, getShowUrl, isImage, isPdf, isVideo, isAudio, fileNameWithoutExtension, fileExtension } from '../utils/fileHandling/files';

interface SearchCasesData {
	request: SearchCaseRequest;
	parsedCriteria: Array<{ label: string; value: any }>;
	tablePage: number;
	casesTotalCount: number;
	filteredCases: CaseResponse[];
	statistics: Array<StatisticResponseWithCaseId | null>;
	loading: boolean;
	errorFetchingData: boolean;
	DecisionType: typeof DecisionType;
}

export default defineComponent({
	name: 'BerichtView',
	data(): SearchCasesData {
		return {
			request: {} as SearchCaseRequest,
			parsedCriteria: [] as Array<{ label: string; value: any }>,
			tablePage: 0,
			casesTotalCount: 0,
			filteredCases: [],
			statistics: [] as Array<StatisticResponseWithCaseId | null>,
			loading: false,
			errorFetchingData: false,
			DecisionType,
		};
	},
	components: {
		LoadingIcon,
		TheBox,
		BerichtItemStatistic,
		Wahlbeteiligung,
		PieChart,
		CategoryBarChart,
		ButtonSticky,
		PdfIcon,
		AudioIcon,
		ImageIcon,
		VideoIcon,
	},
	methods: {
		async doSearch() {
			let searchRequest = prepareSearchRequest(this.request) as SearchCaseRequest;
			if (searchRequest.states) {
				// @ts-ignore
				searchRequest.states = [searchRequest.states];
			}
			// Fasse die Prüfungsstatus zusammen (wichtig für backend search)
			if (searchRequest.states?.includes(CaseState.ExaminationSmallSenate)) {
				searchRequest.states.push(CaseState.ExaminationSmallSenateRunning);
			}
			if (searchRequest.states?.includes(CaseState.ExaminationWerberat)) {
				searchRequest.states.push(CaseState.ExaminationWerberatRunning);
			}

			const searchResult = await searchCases(searchRequest, this.tablePage, undefined, undefined, true);
			if (searchResult.success) {
				if (this.tablePage === 0) {
					this.filteredCases = searchResult.data.items;
				} else {
					this.filteredCases = [...this.filteredCases, ...searchResult.data.items];
				}

				this.filteredCases = this.filteredCases
					.map((c) => {
						return { ...c, caseFiles: getCaseFiles(c) };
					})
					.sort((a, b) => {
						const dateA = a.createdDate ? new Date(a.createdDate).getTime() : Infinity;
						const dateB = b.createdDate ? new Date(b.createdDate).getTime() : Infinity;

						return dateA - dateB; // ascending order
					});
			} else {
				this.filteredCases = [];
				this.errorFetchingData = true;
			}
		},
		async loadStatistics(id) {
			const result = await getCaseStatistics(id);
			if (result.success) {
				return result.data;
			}
			return null;
		},
		async loadAllCases() {
			this.loading = true;
			let cancel = false;

			// Timeout: in case doSearch() has an error and doesn't update the counts correctly
			const timeout = new Promise<void>((_, reject) => {
				setTimeout(() => {
					cancel = true;
					reject(new Error('Timeout reached after 20 seconds.'));
				}, 20000); // 20 seconds
			});

			const loop = async () => {
				while (this.filteredCases.length < this.casesTotalCount && this.errorFetchingData === false) {
					if (cancel) return; // Stop further execution if timeout has occurred
					await this.doSearch();

					// Load statistics for each new case
					const newCases = this.filteredCases.length < 10 ? this.filteredCases : this.filteredCases.slice(0, 10);

					// Load statistics for each case
					await Promise.all(
						newCases.map(async (caseItem) => {
							if (caseItem && caseItem.id) {
								const statistics = await this.loadStatistics(caseItem.id);

								if (statistics) {
									const statisticWithCaseId = { ...statistics, caseId: caseItem.id };
									this.statistics.push(statisticWithCaseId);
								}
							}
						}),
					);

					this.tablePage++;
				}
			};

			try {
				await Promise.race([loop(), timeout]);
			} catch (error) {
				console.error('Timeout', error);
			}

			this.loading = false;
		},
		getStatisticByCaseId(caseId) {
			if (!caseId || this.statistics.length == 0) return null;
			return this.statistics.find((s) => s?.caseId === caseId) as StatisticResponseWithCaseId;
		},
		exportPdf() {
			window.print();
		},
		getCallbacksCount,
		getCaseVoteEnde,
		isSmallSenateDecision,
		hasDecision,
		getShowUrl,
		isImage,
		isPdf,
		isVideo,
		isAudio,
		fileNameWithoutExtension,
		fileExtension,
	},
	mounted() {
		const requestDataString = localStorage.getItem('werberatReportData');

		if (requestDataString !== null) {
			let requestDataParsed = JSON.parse(requestDataString);
			if (requestDataParsed) {
				this.casesTotalCount = requestDataParsed.casesTotalCount ? requestDataParsed.casesTotalCount : 0;

				if (requestDataParsed.request) {
					this.request = requestDataParsed.request;
					this.loadAllCases();

					this.parsedCriteria = Object.entries(this.request)
						.filter(([key, value]) => value !== null && value !== '' && key !== 'fromDate' && key !== 'toDate')
						.map(([key, value]) => {
							let label = '';

							switch (key) {
								case 'searchTerm':
									label = 'Freitext';
									break;
								case 'category':
									label = 'Beschwerdegrund';
									break;
								case 'medium':
									label = 'Medium';
									break;
								case 'states':
									label = 'Status';
									break;
								case 'isPublished':
									label = 'Veröffentlicht';
									break;
								case 'reach':
									label = 'Bundesland';
									break;
								case 'sector':
									label = 'Branche';
									break;
							}

							const displayValue = typeof value === 'object' && value !== null && 'label' in value ? value.label : value;
							return { label, value: displayValue };
						});
				}
			}
		}
	},
});
</script>

<style lang="scss">
#bericht {
	.button-sticky.is-round.el-button {
		.el-icon {
			height: 45px;
		}
	}

	.report__criteria {
		margin: 3rem 0;

		ul {
			list-style-type: none;
			margin: 0;
			margin-top: 1rem;
			padding: 0;

			li.has-status {
				display: inline-block;
				font-size: 1rem;
				font-weight: 500;
				margin-right: 0.5rem;
				margin-bottom: 0.5rem;

				&:last-child {
					margin-right: 0;
				}
			}
		}
	}

	#searchresults {
		.bericht-item {
			margin-bottom: 6rem;

			h3 {
				font-size: 1.4rem;
				font-weight: 500;
			}

			.bericht-item__complaints {
				margin-top: 3rem;
				h3 {
					margin-bottom: 1.5rem;
				}

				.box {
					box-shadow: none;
					border: 1px solid var(--el-color-lightgrey-2);
					border-radius: 5px;
					padding: 0.75rem;

					& + .box {
						margin-top: 1rem;
					}

					.text {
						font-size: 0.9rem;
					}
				}
			}

			.bericht-item__decision {
				margin-top: 3rem;

				.has-status {
					margin-bottom: 2rem;
				}
			}

			.bericht-item__media {
				display: flex;
				flex-direction: row;
				flex-wrap: wrap;
				justify-content: flex-start;
				margin-top: 3rem;

				h3 {
					flex: 100%;
				}

				$mediabox_margin: 1rem;
				.box {
					min-width: calc(25% - $mediabox_margin);
					flex: calc(25% - $mediabox_margin);
					max-width: calc(25% - $mediabox_margin);

					min-height: 100px;
					margin-top: 0;
					margin-right: 1rem;
					margin-bottom: 1rem;
					cursor: pointer;

					&:nth-child(5) {
						margin-right: 0;
					}

					&:hover a {
						text-decoration: underline;
						color: var(--el-color-primary);
					}

					a {
						display: flex;
						flex-direction: column;
						align-items: center;
						justify-content: center;
						color: var(--el-color-primary);

						svg {
							width: 3rem;
							max-height: 3rem;
						}

						.filename {
							display: inline-block;
							margin-top: 1rem;
							max-width: 100%;
							color: var(--el-color-primary);
							white-space: nowrap;
							overflow: hidden;
							text-overflow: ellipsis;
							position: relative;
							cursor: pointer;
						}
					}
				}
			}
		}
	}

	@media screen and (max-width: 900px) {
		#searchresults {
			.bericht-item {
				.bericht-item__media {
					.box {
						flex: calc(33% - 1rem);
						max-width: calc(33% - 1rem);
					}
				}
			}
		}
	}
}
</style>
